/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useTheme } from '@material-ui/core/styles'
import algoliasearch, { SearchClient } from 'algoliasearch/lite'
import { identity, isEqual, pickBy } from 'lodash'
import debounce from 'lodash/debounce'
import { useRouter } from 'next/router'
import qs from 'qs'
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { InstantSearch, InstantSearchProps } from 'react-instantsearch-dom'
import { useScrollToRef } from 'src/lib/hooks/useScrollToRef'
import { Theme } from 'src/lib/theming'

export const APP_ID = 'X0O1H31A99'
const DEBOUNCE_TIME = 300

export type AlgoliaSearchProps = Partial<Omit<InstantSearchProps, 'searchClient'>> & {
  apiKey: string
  appId?: string
  children: ReactNode
  indexName: string
  searchClient?: SearchClient
}

const createURL = (state: Record<string, unknown>) => {
  const effectiveState = pickBy(state, identity)
  return `?q=${btoa(JSON.stringify(effectiveState))}`
}

// Only render queryString if the object is non-empty
const searchStateToUrl = ({ searchState }: { searchState: Record<string, unknown> }) => {
  return searchState !== null &&
    typeof searchState === 'object' &&
    Object.keys(searchState ?? {}).length > 0
    ? `${window.location.pathname}${createURL(searchState)}`
    : window.location.pathname
}

type SearchState = Record<string, unknown>

const pathToSearchState = (path?: string): SearchState => {
  if (!path) return {}

  const params = path.includes('?') ? qs.parse(path.substring(path.indexOf('?') + 1)) : {}
  if (!params.q) return {}

  return JSON.parse(atob(params.q as string)) as SearchState
}

const AlgoliaSearch = ({
  appId = APP_ID,
  apiKey,
  indexName,
  children,
  searchState: initialSearchState = {},
  searchClient,
  ...searchProps
}: AlgoliaSearchProps) => {
  const router = useRouter()
  const path = router?.asPath ?? ''

  const [searchState, setSearchState] = useState<SearchState>(initialSearchState)

  const { htmlElRef, scrollToElement } = useScrollToRef({ behavior: 'smooth', block: 'start' })
  const { spacing } = useTheme<Theme>()

  const ScrollTo = () => (
    <div style={{ position: 'relative' }}>
      <div ref={htmlElRef} style={{ marginTop: spacing(-8), position: 'absolute' }} />
    </div>
  )

  const client: SearchClient = useMemo(() => searchClient ?? algoliasearch(appId, apiKey), [
    apiKey,
    appId,
    searchClient,
  ])

  useEffect(() => {
    const state = pathToSearchState(path)
    if (!isEqual(searchState, state)) {
      setSearchState(state)
    }
  }, [path, searchState])

  const routerPush = router?.push
  const debouncedPushUrl = useCallback(
    async (state: Record<string, unknown>) => {
      const url = searchStateToUrl({ searchState: state })
      await routerPush?.(url, url, { shallow: true })
    },
    [routerPush]
  )

  const onSearchStateChange = useMemo(
    () =>
      debounce(async (state: Record<string, unknown>) => {
        setSearchState(current => {
          if (current.page !== state.page) scrollToElement()
          return isEqual(current, state) ? current : state
        })
        await debouncedPushUrl(state)
      }, DEBOUNCE_TIME),
    [debouncedPushUrl, scrollToElement]
  )

  return (
    <>
      <ScrollTo />
      <InstantSearch
        createURL={createURL}
        indexName={indexName}
        searchClient={client}
        searchState={searchState}
        onSearchStateChange={onSearchStateChange}
        {...searchProps}
      >
        {children}
      </InstantSearch>
    </>
  )
}
export default AlgoliaSearch
